/**
 * Eine Bestellzeile der Bestellliste - `orderListRow` (index.html:7756) samt der von ihr gerufenen
 * Zellendarstellung `orderListCell` (7725), `orderListDate` (7246) und `orderListEditableDisplay`
 * (7650).
 *
 * ================= Die zweite Betriebsart: der Editor (nachgezogen 2026-08-15) ==================
 *
 * `orderListCell` hat eine ZWEITE Betriebsart: steht die Zelle gerade in Bearbeitung
 * (`orderListEditCell?.orderId === o.id && …key === key`), liefert es statt der Anzeige ein
 * Eingabefeld (`orderListEditorCell`, index.html:7657). Dieser Zweig blieb bis zum 2026-08-15
 * ausgelassen - er ist ein SCHREIBPFAD (`commitOrderListEdit` -> `patchOrderListField` -> `save()`,
 * :7628/7690), und der Task-Brief nannte dafuer keine Prop und keinen Testfall.
 *
 * Er ist jetzt da, weil die Luecke teurer war als gedacht: OHNE ihn und ohne den Statuswechsel im
 * Drawer gab es UEBERHAUPT keinen Weg, den Status einer Bestellung zu setzen (das Ziehen zwischen
 * den Board-Gruppen schreibt `boardGroupOverride`, nicht den Status). Was WELCHE Spalte schreibt -
 * und welche zwei gar nicht schreiben koennen -, steht in `lib/orderListEditor.ts`; diese Datei
 * zeichnet nur.
 *
 * Die Anzeige traegt das Verhalten, das ohne den Editor beobachtbar ist, weiterhin vollstaendig:
 * `role="button"`, `tabIndex`, `aria-label` und - fachlich tragend - das `stopPropagation` auf
 * Klick, Kontextmenue und Tastatur. Ohne dieses `stopPropagation` oeffnete jeder Klick auf eine
 * Zelle den Drawer der Zeile (`onclick="openDrawer(...)"` auf dem `<tr>`); mit ihm nicht.
 *
 * Die Baselines bleiben davon unberuehrt: `board-standard` zeigt keine Zelle in Bearbeitung, und
 * die Huelle um die Anzeige ist unveraendert - auch bei den zwei Spalten ohne Schreibweg.
 *
 * ================= Auftrag "Mehrfachauswahl" (Strg/Shift-Klick), nachgezogen 2026-09-01 ==========
 *
 * Das `stopPropagation` aus dem Absatz oben gilt seither NICHT MEHR ausnahmslos: `Bearbeitbar`s
 * `onClick` prueft zuerst `ctrlKey`/`metaKey`/`shiftKey` - ist eine davon gesetzt, bleibt sowohl das
 * `stopPropagation` als auch `onBearbeiten` aus, das Ereignis bubbelt normal zum `<tr>` hoch. Das ist
 * DIE Falle, die dieser Auftrag beheben musste: ein Strg/Shift-Klick GENAU auf einen Zellenwert (der
 * haeufigste Fall - der Text FUELLT die Zeile optisch) haette sonst den Inline-Editor geoeffnet statt
 * die Zeilenauswahl zu treffen. Ohne Zusatztaste bleibt das Verhalten exakt wie oben beschrieben.
 * Die eigentliche Klick-Entscheidung (Auswahl togglen/Bereich markieren/Drawer oeffnen) trifft
 * `BoardSeite.tsx#zeileAngeklickt` - diese Datei reicht nur `onZeileAngeklickt`/`ausgewaehlt` durch,
 * siehe die beiden Props an `BestellzeileProps`.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `monoKeys` enthaelt `tracking`, `invoice`, `p1amount` und `p2amount` - vier Schluessel, die
 *    `REMOVED_ORDER_LIST_KEYS` beim Laden wegwirft. Im Bestand erreichte die Klasse `mono` damit im
 *    lebenden Pfad nur `value`. Uebernommen, nicht bereinigt - seit dem Auftrag "Spalte
 *    Trackingnummer im Bestellboard" ergaenzt um `dsvShipmentNo` (KEIN uebernommener Bestandswert,
 *    siehe `MONO_KEYS` unten), das die Klasse damit als zweite Spalte lebend erreicht.
 * 2. `orderListCell` behandelt im `switch` NUR die elf Vorgabespalten; alles andere - auch die
 *    zehn entfernten Schluessel - faellt in den `default`-Zweig und wird als EIGENES Feld
 *    (`o.customFields?.[key]`) gelesen. Eine wiederauferstandene `p1status`-Spalte zeigte deshalb
 *    nicht den Ratenstatus, sondern ein leeres eigenes Feld.
 * 3. Die Statuszelle faellt bei unbekanntem Status auf `{ label: o.status || '–', color: '#666',
 *    soft: '#eee' }` zurueck - ein Objekt OHNE `icon`/`iconSvg`, weshalb das Symbol dort `list`
 *    wird. `#666`/`#eee` sind Kurzform-Hexwerte, die einzigen im ganzen Board.
 * 4. `orderListDate` markiert nur dann `overdue`/`today`, wenn `markDue` gesetzt ist - und das ist
 *    ausschliesslich in der Spalte „Faelligkeitsdatum" der Fall. Ein ueberfaelliges ETA bleibt
 *    unmarkiert.
 * 5. Die Bestellnummer faellt auf den Text „Ohne Bestellnummer" zurueck, NICHT auf den Namen der
 *    Bestellung (anders als die Kanban-Karte, `index.html:7078`, UND anders als das Kontextmenue,
 *    das auf den Namen zurueckfaellt, bevor es den generischen Platzhalter „Bestellung" zeigt - zwei
 *    woertlich verschiedene Rueckfallketten im selben Bereich, siehe `KontextMenue.tsx`).
 *
 * ================= Task 15: Ziehen =================
 *
 * `row.draggable = !!order && !order.archived` (`index.html:6956`) - KEINE Rechtepruefung (Bericht
 * Task 15, B1). Die eigentliche Bedienung (`dragstart`/`dragend`) steht in `ZeilenUmzug.ts`; hier
 * nur die Verdrahtung ans `<tr>`.
 *
 * AUSNAHME Kontotyp "Nur lesen" (Neubau, `nurLeser`): das Ziehen schreibt `boardGroupOverride`, der Server
 * lehnt es fuer ein Konto mit nur `view_*`-Rechten ab - die Zeile ist dann nicht ziehbar. Das ist keine
 * Rechtepruefung im Sinne von B1 (kein einzelnes Recht entscheidet), sondern der Kontotyp ohne jedes
 * Schreibrecht; jedes Konto mit einem Schreibrecht zieht wie im Bestand.
 */
import { memo, useState } from "react";
import { isRecentOrder } from "../../lib/status.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import { orderListDue } from "../../lib/status.ts";
import { orderTotal } from "../../lib/payments.ts";
import { fmtMoney } from "../../lib/money.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import type { Ansicht } from "../../state/adresse.ts";
import { istEinfacherKlick, Verweis } from "../Verweis.tsx";
import { fmtDate, todayIso } from "../../lib/format.ts";
import { STATUS_ICONS } from "../../config/board.ts";
import type { OrderListColumn } from "../../config/board.ts";
import type { Order, Sendung, Supplier } from "../../lib/types.ts";
import type { DrawerZiel } from "../../lib/zellZiel.ts";
import { orderListEditorAria, orderListEditorRegel, orderListEditorWert } from "../../lib/orderListEditor.ts";
import type { EditorRegel } from "../../lib/orderListEditor.ts";
import { eingebuchtZustand, letzteEinbuchungVon, orderListZustand } from "../../lib/orderList.ts";
import { zollinstruktionenAngestossen } from "../../lib/zollinstruktionen.ts";
import { orderSupplier } from "../../lib/suppliers.ts";
import { supplierRegion } from "../../lib/laender.ts";
import { Ikone } from "../Ikone.tsx";
import { Flagge } from "../Flagge.tsx";
import { BestellungSplitMarke, useIstTeillieferung } from "../SplitMarke.tsx";
import { StockMarke } from "../StockMarke.tsx";
import { NeuerArtikelMarke } from "../NeuerArtikelMarke.tsx";
import { zeileZiehenEnde, zeileZiehenStart } from "./ZeilenUmzug.ts";
import type { ZeilenUmzugRef } from "./ZeilenUmzug.ts";

/**
 * index.html:7757 - die Spalten, deren Zelle die Klasse `mono` traegt. `dsvShipmentNo` (Auftrag
 * "Spalte Trackingnummer im Bestellboard") ist NICHT aus dem Bestand uebernommen - die Spalte gab es
 * dort nicht -, sondern bewusst ergaenzt: eine Sendungsnummer wie `SRAF0347676` ist ein
 * alphanumerischer Code, keine Fliesstext-Spalte, dieselbe Kategorie wie die entfernte `tracking`
 * oben (die REMOVED_ORDER_LIST_KEYS beim Laden verwirft) und wie `invoice`.
 */
export const MONO_KEYS = new Set(["tracking", "invoice", "value", "p1amount", "p2amount", "dsvShipmentNo"]);

const ikonenTabelle = STATUS_ICONS as unknown as Record<string, string>;

/** index.html:7246 - `<span class="order-list-date…">` bzw. der Gedankenstrich. */
export function Datum({ wert, markDue = false }: { wert: unknown; markDue?: boolean }) {
  if (!wert) return <span className="order-list-empty">–</span>;
  const value = wert as string;
  const cls = markDue
    ? value < todayIso()
      ? " overdue"
      : value === todayIso()
        ? " today"
        : ""
    : "";
  return <span className={`order-list-date${cls}`}>{fmtDate(value)}</span>;
}

/**
 * Die Pille der Spalte „Ware eingebucht" (Asana „Ware eingebucht Knopf - Sehr wichtig fuer
 * Steuerberater", 2026-10-20) - auch von der Sammelsendungs-Elternzeile benutzt.
 *
 * „vollständig · <Datum>" gruen (`paid`), „teilweise · <Datum>" orange (`open`); `datum` ist der Tag
 * der letzten Einbuchung, im selben Format wie die uebrigen Datumsspalten (`fmtDate`). Ohne Datum
 * (`null` - z. B. eine als vollstaendig gewertete Bestellung ohne lesbare Lagerbuchung) steht nur das
 * Zustandswort, nie "Invalid Date". Die Klasse `eingebucht` hebt die 180-px-Grenze der Pille auf
 * (`styles/neubau/wareEingebucht.css`) - Zustand plus Datum passen sonst nicht hinein.
 */
export function EingebuchtPille({
  zustand,
  datum,
  titel,
}: {
  zustand: "vollstaendig" | "teilweise";
  datum: string | null;
  titel?: string;
}) {
  const vollstaendig = zustand === "vollstaendig";
  const wort = vollstaendig ? "vollständig" : "teilweise";
  const text = datum ? `${wort} · ${fmtDate(datum)}` : wort;
  return (
    // `title`: ohne ausdruecklichen Text der volle Pillentext - in einer schmal gezogenen Spalte kuerzt
    // die Zelle ihn mit Auslassungspunkten.
    <span className={`order-list-pill eingebucht ${vollstaendig ? "paid" : "open"}`} title={titel ?? text}>
      <Ikone name={vollstaendig ? "package-check" : "circle-dashed"} className="status-icon" />
      {text}
    </span>
  );
}

/** Die Pille der Spalte „Zahlung anstoßen" - auch von der Sammelsendungs-Elternzeile benutzt. */
export function ZahlungPille() {
  return (
    <span className="order-list-pill paid">
      <Ikone name="circle-check" className="status-icon" />
      Zahlung angestoßen
    </span>
  );
}

/**
 * Die Plakette der Spalte „Zollinstruktionen anstoßen" - Bauart wie `ZahlungPille`, auch von der
 * Sammelsendungs-Elternzeile benutzt (`AggregatZellen.tsx`).
 */
export function ZollPille() {
  return (
    <span className="order-list-pill paid">
      <Ikone name="circle-check" className="status-icon" />
      Zollinstruktionen angestoßen
    </span>
  );
}

/** Die Statuspille (Eigenheit 3: unbekannter Status faellt auf grau zurueck) - auch von der Elternzeile benutzt. */
export function StatusPille({ definitionen, status }: { definitionen: StatusDefinitionen; status: string }) {
  const st = definitionen.STATUS_MAP[status] || {
    label: status || "–",
    color: "#666",
    soft: "#eee",
  };
  return (
    <span className="order-list-status" style={{ background: st.soft as string, color: st.color as string }}>
      <Ikone name={(st.iconSvg as string) || ikonenTabelle[st.icon as string] || "list"} className="status-icon" />
      {st.label as string}
    </span>
  );
}

export interface BestellzeileProps {
  bestellung: Order;
  spalten: readonly OrderListColumn[];
  definitionen: StatusDefinitionen;
  lieferanten: readonly Supplier[] | null | undefined;
  /** Aufgabe 5 (Tieflinks - echte Anker): einmal in `BoardSeite.tsx` gelesen und durchgereicht -
   *  NICHT hier per `useAnsicht()` nochmal, geschweige denn einmal je Zelle (Befund W-5 der
   *  Pruefrunde 1: das waeren neun `useLocation`/`lesen()`-Aufrufe je Zeile, ungebraucht fuer acht
   *  der neun Spalten). Nur die Bestellnummer-Zelle unten braucht sie. */
  ansicht: Ansicht;
  /**
   * `openDrawer(o.id)`, index.html:7759. Das optionale `ziel` (Reiter und Abschnitt) braucht nur die Artwork-Marke
   * (`ArtworkMarke`); alle übrigen Aufrufer in dieser Datei rufen mit der Id allein.
   */
  onOeffnen?: (id: string, ziel?: DrawerZiel) => void;
  /** `openOrderContextMenu(event, o.id)`, index.html:7759 - Task 15. */
  onKontextmenue?: (ereignis: React.MouseEvent, id: string) => void;
  /** `startOrderListEdit(o.id, key)`, index.html:7651. */
  onBearbeiten?: (id: string, key: string) => void;
  /**
   * Der Schluessel der Zelle DIESER Zeile, die gerade in Bearbeitung steht - im Bestand das
   * Modul-Global `orderListEditCell` (index.html:4651), das den Vergleich `…orderId === o.id &&
   * …key === key` bei JEDER Zelle anstellt (:7726). Hier trifft der Aufrufer die Zeilenhaelfte des
   * Vergleichs schon vor dem Zeichnen, die Zelle nur noch ihre eigene.
   */
  bearbeitet?: string | null;
  /** `commitOrderListEdit(id, key, value)`, index.html:7628. */
  onUebernehmen?: (id: string, key: string, wert: string) => void;
  /** `cancelOrderListEdit(id, key)`, index.html:7622. */
  onAbbrechen?: (id: string, key: string) => void;
  /**
   * Auftrag "Mehrfachauswahl" (Strg/Shift-Klick auf der Bestellübersicht): der VOLLE Klick-Handler
   * der Zeile - ERSETZT das bisherige `onClick={() => onOeffnen?.(o.id)}`. `BoardSeite` liest den
   * Zusatztastenstand des durchgereichten `MouseEvent` selbst (`zeileAngeklickt`, `BoardSeite.tsx`)
   * und entscheidet dort, ob der Klick den Drawer öffnet oder die Zeile markiert/entmarkiert - diese
   * Komponente trifft dazu KEINE eigene Entscheidung, sie reicht Ereignis und Id nur durch.
   */
  onZeileAngeklickt?: (ereignis: React.MouseEvent, id: string) => void;
  /**
   * Auftrag "Mehrfachauswahl": `true`, wenn diese Zeile aktuell in `BoardSeite`s Auswahl steht.
   * Setzt zusätzlich zu `list-recent` die Klasse `order-list-row-selected`
   * (`styles/neubau/mehrfachauswahl.css`) - beide Klassen können gleichzeitig stehen.
   */
  ausgewaehlt?: boolean;
  /**
   * Die verknuepfte DSV-Sendung - entscheidet ueber die Bauart des Editors bei `etd`/`eta`, siehe
   * `lib/orderListEditor.ts`. Die Bestellliste hat seit dem Auftrag „Sendungsabruf beim App-Start
   * entfernen" (2026-08-22) keine Quelle dafuer und reicht diese Prop nirgends mehr durch - sie
   * bleibt hier stehen (samt Vorgabewert `null`), weil `lib/orderListEditor.ts#orderListEditorRegel`
   * sie weiterhin braucht; woher sie kaeme, muesste eine Sammelroute je Bestellliste liefern (heute
   * nur `GET /api/orders/:id/shipment`, gezielt fuer EINE Bestellung - siehe
   * `docs/superpowers/decisions/2026-08-22-sendungen-app-start.md`).
   */
  sendung?: Sendung | null;
  /** `enableBoardDragAndDrop`s `dragstart`/`dragend` (index.html:6957-6971) - Task 15. */
  umzugRef?: ZeilenUmzugRef;
  /**
   * Task 6 (Sammelsendung-Elternzeile): `true` fuer eine Zeile, die unter einer aufgeklappten
   * `Sammelsendungszeile` steht - setzt `sammelsendung-kind` (Einrueckung,
   * `styles/neubau/sammelsendung.css`). Ohne die Prop (Normalfall) bleibt das DOM unveraendert -
   * tragend fuer die Baseline-Strukturtests.
   */
  eingerueckt?: boolean;
  /** Kontotyp "Nur lesen" (`state/nurLeser.ts`): die Zeile ist nicht ziehbar. Ohne Angabe: ziehbar. */
  nurLeser?: boolean;
}

function BestellzeileKomponente({
  bestellung: o,
  spalten,
  definitionen,
  lieferanten,
  ansicht,
  onOeffnen,
  onKontextmenue,
  onBearbeiten,
  bearbeitet,
  onUebernehmen,
  onAbbrechen,
  sendung = null,
  umzugRef,
  onZeileAngeklickt,
  ausgewaehlt,
  eingerueckt,
  nurLeser = false,
}: BestellzeileProps) {
  /**
   * AUFTRAG 07 (E4): eine in JTL geloeschte Bestellung ist streng schreibgeschuetzt. Die Ableitung
   * steht NICHT hier, sondern in `lib/orderList.ts#orderListZustand` - dieselbe Regel traegt auch die
   * Sichtbarkeitsfilterung (`BoardSeite.tsx`), den Zug-Guard (`ZeilenUmzug.ts`) und die
   * Auswahlsperre. Eine zweite Kopie hier waere eine zweite Wahrheit.
   */
  const geloescht = orderListZustand(o) === "deleted";
  // Teillieferung (Damian, 16.10.2026): dieselbe Erkennung wie die graue Marke neben der Nummer.
  // Die Klasse faerbt die ganze Zeile orange (`styles/neubau/teillieferung.css`).
  const teillieferung = useIstTeillieferung(o);
  return (
    <tr
      data-order-id={o.id as string}
      // Auftrag "Mehrfachauswahl": `order-list-row-selected` NEBEN `list-recent`, nicht ersetzend -
      // beide Klassen koennen gleichzeitig stehen (siehe Prop-Kommentar `ausgewaehlt` oben). Ohne
      // Auswahl (der Normalfall) bleibt die Klasse exakt wie vor diesem Auftrag: entweder leer oder
      // nur `list-recent` - tragend fuer die unveraenderten Baseline-Strukturtests.
      //
      // AUFTRAG 07 (E4): `order-list-row-deleted` kommt als DRITTE Klasse dazu. Sie ist der einzige
      // Griff, den die Optik (`styles/neubau/jtlGeloeschtZeile.css`) braucht, und sie aendert an den
      // bestehenden Klassen nichts - die Baseline zeigt keine geloeschte Zeile.
      className={[
        isRecentOrder(o) ? "list-recent" : "",
        ausgewaehlt ? "order-list-row-selected" : "",
        geloescht ? "order-list-row-deleted" : "",
        eingerueckt ? "sammelsendung-kind" : "",
        teillieferung ? "teillieferung-zeile" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      // Eigenheit 1 (index.html:6956): archivierte Zeilen sind nicht ziehbar - KEINE Rechtepruefung.
      // AUFTRAG 07 (E4): geloeschte Zeilen ebenfalls nicht - sie sind schreibgeschuetzt.
      // Kontotyp "Nur lesen": ebenfalls nicht ziehbar, und ohne dragstart-Handler - die Zeile traegt dann nie eine
      // Id in `umzugRef`, ein Drop findet nichts zum Ablegen.
      draggable={!o.archived && !geloescht && !nurLeser}
      onDragStart={(ereignis) =>
        umzugRef && !nurLeser && zeileZiehenStart(ereignis, o, umzugRef)
      }
      onDragEnd={(ereignis) => umzugRef && !nurLeser && zeileZiehenEnde(ereignis, umzugRef)}
      // AUFTRAG 07 (E4): KEIN Zeilenklick und KEIN Kontextmenue fuer geloeschte Zeilen. Beide
      // Handler fallen ganz weg (nicht nur "tun nichts"): ein Klick wuerde sonst den Drawer oeffnen -
      // die Bestellung existiert in JTL nicht mehr, ein Schreib- oder Navigationsversuch dort
      // scheitert. `onKontextmenue` wird damit erst gar nicht gerufen, `KontextMenue.tsx` bleibt
      // zusaetzlich defensiv (dortige zweite Pruefung).
      onClick={geloescht ? undefined : (ereignis) => onZeileAngeklickt?.(ereignis, o.id as string)}
      onContextMenu={geloescht ? undefined : (ereignis) => onKontextmenue?.(ereignis, o.id as string)}
    >
      {spalten.map((spalte, index) => (
        <td key={spalte.key} className={MONO_KEYS.has(spalte.key) ? "mono" : ""}>
          {index === 0 && !geloescht ? <GruppenMarke bestellung={o} onOeffnen={onOeffnen} /> : null}
          {index === 0 && !geloescht ? <ArtworkMarke bestellung={o} onOeffnen={onOeffnen} /> : null}
          {index === 0 && geloescht ? (
            // AUFTRAG 07 (E4): die geforderte Anzeige „In JTL geloescht" - ein Textknoten, damit die
            // Bedingung im Strukturtest pruefbar ist. Er sitzt in der ERSTEN Zelle (dort steht auch
            // die Gruppenmarke), die Standdaten der uebrigen Spalten bleiben unveraendert stehen
            // ("plus die einbehaltenen Standdaten", Auftrag).
            <span className="order-list-deleted-badge">In JTL gelöscht</span>
          ) : null}
          <Zelle
            bestellung={o}
            onOeffnen={onOeffnen}
            ansicht={ansicht}
            schluessel={spalte.key}
            definitionen={definitionen}
            lieferanten={lieferanten}
            onBearbeiten={onBearbeiten}
            // AUFTRAG 07 (E4): geloeschte Zeilen sind STRENG SCHREIBGESCHUETZT - eine Zeile, die
            // gerade in Bearbeitung steht, waehrend die Bestellung in JTL geloescht wird (SSE), darf
            // deshalb nicht weiter offen bleiben.
            inBearbeitung={!geloescht && bearbeitet === spalte.key}
            // AUFTRAG 07 (E4): der Schalter, der aus der Anzeige die REINE Anzeige macht - ohne
            // Bearbeitungs-Huelle, ohne Detaillink, ohne Editor. Siehe `Zelle`.
            schreibgeschuetzt={geloescht}
            onUebernehmen={onUebernehmen}
            onAbbrechen={onAbbrechen}
            sendung={sendung}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * Auftrag "Bestellboard-Filter — Renderausfall durch fehlende Memoisierung beheben" (2026-09-06):
 * `React.memo` OHNE eigenen Vergleicher (Standard-Flachvergleich reicht) - GEMESSEN auf Produktion,
 * das Oeffnen EINES Spaltenfilters (`geoeffneterSpaltenfilter`, lokaler State EINE Ebene UEBER allen
 * Zeilen in `BoardSeite.tsx`) loeste OHNE diese Zeile einen Re-Render JEDER Zeile aus, obwohl sich an
 * keiner einzigen etwas geaendert hatte - bei mehreren Dutzend/hundert Zeilen sichtbar als 30-90+
 * Sekunden Stillstand nach dem Klick. Dieser `memo` allein reicht NICHT: `BoardSeite.tsx` muss dazu
 * zusaetzlich jede an `Bestellzeile` durchgereichte Callback-Prop referenzstabil halten
 * (`useCallback`, siehe dortiger Kommentar) - sonst waere JEDE Prop bei jedem Render ohnehin neu und
 * der Flachvergleich hier faende trotzdem nie Gleichheit.
 */
export const Bestellzeile = memo(BestellzeileKomponente);

function GruppenMarke({ bestellung, onOeffnen }: Pick<BestellzeileProps, "bestellung" | "onOeffnen">) {
  if (bestellung.orderGroupStatus !== "active" || !bestellung.orderGroupMemberCount) return null;
  return (
    <span
      className="order-group-label"
      role="button"
      tabIndex={0}
      onClick={(ereignis) => {
        ereignis.stopPropagation();
        onOeffnen?.(bestellung.id as string);
      }}
      onKeyDown={(ereignis) => {
        if (ereignis.key === "Enter" || ereignis.key === " ") {
          ereignis.preventDefault();
          ereignis.stopPropagation();
          onOeffnen?.(bestellung.id as string);
        }
      }}
    >
      {`Zusammengehörig · ${bestellung.orderGroupMemberCount} Bestellungen`}
    </span>
  );
}

/** Wohin die Artwork-Marke führt: der Abschnitt Positionsdokumente im Reiter Dokumente (`DokumenteTab.tsx`, `docs-positions`). */
const ARTWORK_ZIEL: DrawerZiel = { tab: "docs", abschnitt: "docs-positions" };

/**
 * Artwork-Abgleich (Spec „Im Board“): hat die Bestellung offene Prüfwarnungen (`artworkWarnungen`, das Backend zählt nur Warnungen
 * der gültigen Fassung, die noch niemand als gesehen markiert hat - hier genügt die Zahl), steht „Artwork prüfen“ in der ersten
 * Zelle. Der Name ist der, den `oeffentlich()` aus der Spalte `artwork_warnungen` der Bestellliste macht (camelCase wie
 * `dsvShipmentNo`); `bestellzeileArtworkMarke.test.tsx` schickt den Wert durch das echte `oeffentlich()`. Ein Klick öffnet den
 * Drawer an den Positionsdokumenten, wo das Abzeichen die Einzelheiten zeigt; die Zeile klickt er nicht zusätzlich an. Wie
 * `GruppenMarke` ruft die Marke dazu bewusst `onOeffnen` direkt statt des Zeilenklicks (`zeileOeffnen` in `BoardSeite.tsx`): eine
 * Mehrfachauswahl bleibt nach dem Klick stehen. Eine in JTL gelöschte Zeile ist schreibgeschützt und hat die Marke nicht (siehe oben).
 */
function ArtworkMarke({ bestellung, onOeffnen }: Pick<BestellzeileProps, "bestellung" | "onOeffnen">) {
  const anzahl = bestellung.artworkWarnungen;
  if (typeof anzahl !== "number" || !(anzahl > 0)) return null;
  return (
    <button
      type="button"
      className="order-list-artwork-marke"
      title={`Artwork-Prüfung: Warnung an ${anzahl === 1 ? "1 Position" : `${anzahl} Positionen`}`}
      onClick={(ereignis) => {
        ereignis.stopPropagation();
        onOeffnen?.(bestellung.id as string, { ...ARTWORK_ZIEL });
      }}
      // Wie bei den übrigen Knöpfen der Zeile: die Tasten gehören dem Knopf, nicht den Tastenkürzeln der Tabelle darüber.
      onKeyDown={(ereignis) => ereignis.stopPropagation()}
    >
      Artwork prüfen
    </button>
  );
}

/** index.html:7650 - die Huelle, die aus einer Anzeige eine anklickbare Bearbeitungsflaeche macht. */
function Bearbeitbar({
  bestellung: o,
  schluessel,
  label,
  className = "",
  beschreibungId,
  titel,
  onBearbeiten,
  children,
}: {
  bestellung: Order;
  schluessel: string;
  label: string;
  className?: string;
  /** Id eines Elements im Inneren, das den Knopf beschreibt (`aria-describedby`). */
  beschreibungId?: string;
  /** Der Grund, warum die Zelle im Grid nicht aenderbar ist (`title`) - der Klick oeffnet dann den Drawer. */
  titel?: string;
  onBearbeiten?: (id: string, key: string) => void;
  children: React.ReactNode;
}) {
  const oeffnen = () => onBearbeiten?.(o.id as string, schluessel);
  return (
    <span
      className={`order-list-editable-value ${className}`}
      role="button"
      tabIndex={0}
      aria-label={`${label} bearbeiten`}
      aria-describedby={beschreibungId}
      title={titel}
      onClick={(ereignis) => {
        // Auftrag "Mehrfachauswahl" (Strg/Shift-Klick): ein Klick MIT Zusatztaste gilt der
        // Zeilenauswahl, nicht der Zellenbearbeitung - GENAU der Fall, den der Kopfkommentar dieser
        // Datei ("Die Falle, die dieser Auftrag beheben muss") beschreibt: der Text FUELLT die Zelle
        // optisch, ein Strg/Shift-Klick GENAU darauf ist der haeufigste Fall. KEIN stopPropagation,
        // KEIN onBearbeiten - das Ereignis bubbelt unveraendert zum `<tr>` (`BoardSeite#zeileAngeklickt`).
        if (ereignis.ctrlKey || ereignis.metaKey || ereignis.shiftKey) return;
        // index.html:7651: `event.stopPropagation();startOrderListEdit(...)`. Ohne das
        // stopPropagation oeffnete der Klick zusaetzlich den Drawer der Zeile.
        ereignis.stopPropagation();
        oeffnen();
      }}
      onContextMenu={(ereignis) => ereignis.stopPropagation()}
      onKeyDown={(ereignis) => {
        if (ereignis.key === "Enter" || ereignis.key === " ") {
          ereignis.preventDefault();
          ereignis.stopPropagation();
          oeffnen();
        } else {
          ereignis.stopPropagation();
        }
      }}
    >
      {children}
    </span>
  );
}

/**
 * index.html:7657 - die zweite Betriebsart der Zelle: das Bedienelement statt der Anzeige.
 *
 * Der Wert lebt hier lokal, nicht am Aufrufer: der Bestand arbeitet uncontrolled (`value=` als
 * Anfangswert, `this.value` beim Uebernehmen) und zeichnet die Tabelle waehrend des Tippens NICHT
 * neu. Ein Zustand weiter oben brauchte je Tastendruck ein Neuzeichnen der ganzen Liste, um dasselbe
 * zu erreichen.
 *
 * Der Fokus kommt ueber einen Callback-Ref statt `autoFocus` und statt des `requestAnimationFrame`
 * aus `startOrderListEdit` (:7613): der Ref laeuft, sobald der Knoten haengt, also genau einmal und
 * ohne Bildschirmtakt - dieselbe beobachtbare Wirkung (fokussiert, und bei Textfeldern der Inhalt
 * markiert), ohne einen Zeitgeber, den ein Test abwarten muesste.
 */
function ZellenEditor({
  bestellung: o,
  schluessel: key,
  regel,
  definitionen,
  onUebernehmen,
  onAbbrechen,
}: {
  bestellung: Order;
  schluessel: string;
  regel: EditorRegel;
  definitionen: StatusDefinitionen;
  onUebernehmen?: (id: string, key: string, wert: string) => void;
  onAbbrechen?: (id: string, key: string) => void;
}) {
  const id = o.id as string;
  const [wert, setzeWert] = useState(() => orderListEditorWert(o, key));
  const uebernehmen = (neu: string): void => onUebernehmen?.(id, key, neu);
  const abbrechen = (): void => onAbbrechen?.(id, key);

  /** `orderListEditorKeydown` (index.html:7641) - Escape verwirft, Enter schliesst das Textfeld. */
  const taste = (ereignis: React.KeyboardEvent<HTMLElement>): void => {
    ereignis.stopPropagation();
    if (ereignis.key === "Escape") {
      ereignis.preventDefault();
      abbrechen();
    } else if (ereignis.key === "Enter" && ereignis.currentTarget.tagName === "INPUT") {
      ereignis.preventDefault();
      (ereignis.currentTarget as HTMLInputElement).blur();
    }
  };

  // `common` im Bestand (:7659): der Klick darf weder den Drawer oeffnen noch das Kontextmenue.
  const gemeinsam = {
    "data-order-list-editor": "true",
    className: regel.klasse,
    "aria-label": orderListEditorAria(regel, o),
    onClick: (ereignis: React.MouseEvent) => ereignis.stopPropagation(),
    onContextMenu: (ereignis: React.MouseEvent) => ereignis.stopPropagation(),
    onKeyDown: taste,
  };
  const fokus = (knoten: HTMLInputElement | HTMLSelectElement | null): void => {
    if (!knoten) return;
    knoten.focus();
    // :7617 - markiert wird NUR in Textfeldern, nicht im Datumsfeld und nicht in der Auswahl.
    if (knoten instanceof HTMLInputElement && knoten.type !== "date") knoten.select();
  };

  if (regel.art === "status") {
    return (
      <select
        {...gemeinsam}
        ref={fokus}
        value={wert}
        // `commitOnChange` (:7661): uebernehmen bei `change`, verwerfen bei `blur`.
        onChange={(ereignis) => {
          setzeWert(ereignis.currentTarget.value);
          uebernehmen(ereignis.currentTarget.value);
        }}
        onBlur={abbrechen}
      >
        {definitionen.STATUSES.map((status) => (
          <option key={status.id as string} value={status.id as string}>
            {status.label as string}
          </option>
        ))}
      </select>
    );
  }
  if (regel.art === "datum") {
    return (
      <input
        {...gemeinsam}
        ref={fokus}
        type="date"
        value={wert}
        onChange={(ereignis) => {
          setzeWert(ereignis.currentTarget.value);
          uebernehmen(ereignis.currentTarget.value);
        }}
        onBlur={abbrechen}
      />
    );
  }
  // Text und Betrag: `commitOnBlur` (:7660) - der Wert geht beim Verlassen hinaus, nicht bei jedem
  // Tastendruck. `inputMode="decimal"` und der Platzhalter stehen nur am Betragsfeld (:7675).
  return (
    <input
      {...gemeinsam}
      ref={fokus}
      type="text"
      {...(regel.art === "betrag" ? { inputMode: "decimal" as const, placeholder: "0,00" } : { placeholder: "–" })}
      value={wert}
      onChange={(ereignis) => setzeWert(ereignis.currentTarget.value)}
      onBlur={(ereignis) => uebernehmen(ereignis.currentTarget.value)}
    />
  );
}

/** index.html:7725 - die Anzeige EINER Zelle, bzw. seit dem 2026-08-15 auch ihr Editor (:7726). */
/** Spalten, deren Wert bei einem Sammelsendungs-Mitglied zentral von der Sendung kommt. */
const UEBERNOMMENE_KOPFFELDER: ReadonlySet<string> = new Set(["status", "invoiceNo"]);

function Zelle({
  bestellung: o,
  schluessel: key,
  definitionen,
  lieferanten,
  ansicht,
  onOeffnen,
  onBearbeiten,
  inBearbeitung,
  schreibgeschuetzt = false,
  onUebernehmen,
  onAbbrechen,
  sendung,
}: {
  bestellung: Order;
  schluessel: string;
  definitionen: StatusDefinitionen;
  lieferanten: readonly Supplier[] | null | undefined;
  /** Von `Bestellzeile.tsx` durchgereicht, dort einmal aus `BoardSeite.tsx` - siehe die
   *  Begruendung an `BestellzeileProps.ansicht` (Befund W-5 der Pruefrunde 1). */
  ansicht: Ansicht;
  /** Von `Bestellzeile.tsx` durchgereicht - fuer die Bestellnummer-Zelle unten (Befund K-1 der
   *  Pruefrunde 1, siehe dort). */
  onOeffnen?: (id: string) => void;
  onBearbeiten?: (id: string, key: string) => void;
  inBearbeitung?: boolean;
  /**
   * AUFTRAG 07 (E4): `true` fuer eine in JTL geloeschte Bestellung. Schaltet JEDEN Schreibweg dieser
   * Zelle ab (`Bearbeitbar`), den Detaillink der Bestellnummer und die `is-block`-Huelle - die Zelle
   * zeigt dann nur noch ihren Text. Siehe `Bestellzeile.tsx` (dort wird der Wert aus
   * `orderListZustand` abgeleitet).
   */
  schreibgeschuetzt?: boolean;
  onUebernehmen?: (id: string, key: string, wert: string) => void;
  onAbbrechen?: (id: string, key: string) => void;
  sendung?: Sendung | null;
}) {
  // :7726, die erste Zeile der Funktion: steht diese Zelle in Bearbeitung, endet sie hier.
  if (inBearbeitung) {
    const entscheidung = orderListEditorRegel(key, sendung ?? null, o);
    // Eine gesperrte Spalte kann gar nicht in Bearbeitung stehen (`BoardSeite` laesst sie nicht
    // hinein) - der Zweig steht fuer den Fall, dass sich die Sperre WAEHREND der Bearbeitung
    // ergibt (eine Sendung wird verknuepft, waehrend das ETA-Feld offen ist). Dann faellt die Zelle
    // auf die Anzeige zurueck, statt ein Feld anzubieten, dessen Uebernahme der Server ablehnt.
    if (entscheidung.art === "editor") {
      return (
        <ZellenEditor
          bestellung={o}
          schluessel={key}
          regel={entscheidung.regel}
          definitionen={definitionen}
          onUebernehmen={onUebernehmen}
          onAbbrechen={onAbbrechen}
        />
      );
    }
  }
  const label = (o.orderNo as string) || "Ohne Bestellnummer";
  // Spec Board (Sammelsendung-Elternzeile): die von der Sendung uebernommenen Kopffelder eines
  // Mitglieds (Lieferant, Status, Rechnungsnummer) tragen einen Hinweis auf die Sendung. Mitglied
  // ist, wer `shipmentParent` mit mindestens zwei Mitgliedern traegt - nie `shipmentOrderGroup`.
  // Muster wie das Transportart-Icon oben: `role="img"` mit Name, Text nur fuer Hilfstechnik/Tooltip.
  const elternSendung =
    o.shipmentParent && o.shipmentParent.memberCount >= 2 ? o.shipmentParent.dsvShipmentNo : null;
  const hinweisText = elternSendung ? `aus Sammelsendung ${elternSendung}` : "";
  const hinweisId = `sammelsendung-hinweis-${String(o.id)}-${key}`;
  const huelle = (
    beschriftung: string,
    inhalt: React.ReactNode,
    className?: string,
  ) => {
    const mitHinweis = elternSendung !== null && UEBERNOMMENE_KOPFFELDER.has(key);
    // AUFTRAG 07 (E4): eine in JTL geloeschte Bestellung bekommt KEINE Bearbeitungs-Huelle. Die
    // Huelle ist die einzige Stelle, die `role="button"`, `tabIndex`, `aria-label` UND den
    // Bearbeitungsweg traegt - eine geloeschte Zeile darf nichts davon haben. Der reine Inhalt
    // (derselbe Text) bleibt stehen: Anzeige heisst hier nicht "leer", sondern "nur lesen".
    if (schreibgeschuetzt) {
      // Eine geloeschte Bestellung ist kein Mitglied mehr (keine Klammer), nennt die Sendung aber weiter.
      return (
        <span className={`order-list-readonly-value${className ? ` ${className}` : ""}`}>
          {inhalt}
          {mitHinweis ? (
            <span className="sammelsendung-kindhinweis" role="img" title={hinweisText} aria-label={hinweisText}>
              <Ikone name="combine" />
            </span>
          ) : null}
        </span>
      );
    }
    // Auftrag "Klick auf nicht aenderbare Grid-Zelle oeffnet die Bestellung": der bisherige Grund
    // (`NICHT_BEARBEITBAR`, Zeilensperre) bleibt als Tooltip stehen; der Klick oeffnet die Bestellung.
    const entscheidung = orderListEditorRegel(key, null, o);
    const sperrgrund = entscheidung.art === "gesperrt" ? entscheidung.grund : undefined;
    return (
      // Der Hinweis steckt im `role="button"` mit `aria-label`: dessen Kinder sind fuer Hilfstechnik
      // "presentational", der Hinweis kaeme so nie an. `aria-describedby` am Knopf verbindet ihn trotzdem.
      <Bearbeitbar
        bestellung={o}
        schluessel={key}
        label={beschriftung}
        className={className}
        beschreibungId={mitHinweis ? hinweisId : undefined}
        titel={sperrgrund}
        onBearbeiten={onBearbeiten}
      >
        {inhalt}
        {mitHinweis ? (
          <span
            id={hinweisId}
            className="sammelsendung-kindhinweis"
            role="img"
            title={hinweisText}
            aria-label={hinweisText}
          >
            <Ikone name="combine" />
          </span>
        ) : null}
      </Bearbeitbar>
    );
  };
  const eingebucht = eingebuchtZustand(o);
  const booked =
    eingebucht === "nichts" ? (
      <span className="order-list-empty">–</span>
    ) : (
      <EingebuchtPille zustand={eingebucht} datum={letzteEinbuchungVon(o)} />
    );
  // Auftrag "Zahlung anstoßen Knopf + Einfügung ... in der Übersicht" (Asana Task 1218171668075282,
  // 2026-09-04): direktes Lesen von `paymentInitiatedAt`, KEIN Aufruf von
  // `orderListFacet`/`orderListSortValue` - dasselbe Muster wie `booked`/`vollstaendigGeliefert`
  // oben (siehe deren Kopfkommentar in `orderList.test.ts`, Grund: diese Funktionen dienen Sortierung
  // und Spaltenfilter, nicht der Zellendarstellung).
  const payment = o.paymentInitiatedAt ? (
    <ZahlungPille />
  ) : (
    <span className="order-list-empty">–</span>
  );
  switch (key) {
    case "name":
      return huelle(
        "Bestellnummer",
        <span className="order-list-name-copy" title={label}>
          {/*
           * Aufgabe 5 (Tieflinks - echte Anker): der Anker WRAPPT den bisherigen Textinhalt, statt
           * ihn zu ersetzen - die Klasse `order-list-name-copy` bleibt woertlich auf dieser Huelle
           * stehen (§ Falle 4). Er steckt INNERHALB der Bearbeitbar-Huelle (`huelle()`/`<Bearbeitbar>`
           * unten), nicht darum: nur so ist der Anker selbst der Klick-ZIEL-Knoten, und seine eigene
           * Navigation laeuft, BEVOR `Bearbeitbar`s `stopPropagation` (index.html:7651-Aequivalent)
           * die Blase stoppt. Waere es umgekehrt (Anker aussen, Bearbeitbar-Huelle innen), waere die
           * Huelle selbst das Klickziel - ihr `stopPropagation` verschluckte den Klick, BEVOR er den
           * Anker je erreicht, und aus dem echten Link-Klick wuerde eine volle Browser-Navigation
           * (kein `preventDefault` liefe mehr).
           *
           * `onClick` auf dem ANKER selbst (Befund K-1 der Pruefrunde 1): derselbe `stopPropagation`
           * schluckt auch den Zeilen-`onClick` (`onOeffnen` -> `drawerOeffnen`, samt
           * `versende({art:"geoeffnet"})` fuer die SSE-Konflikterkennung) - fuer JEDEN Klick auf
           * diese Zelle, mit oder ohne Anker. Ohne dieses `onClick` bliebe die SSE-Erkennung fuer
           * genau diese Bestellung unscharf, obwohl der Drawer (ueber die Navigation) sichtbar
           * oeffnet. `istEinfacherKlick` (Befund W-1): NUR ein Klick ohne Zusatztaste loest
           * `onOeffnen` aus - sonst risse ein Strg+Klick (neuer Tab) die aktuelle Ansicht mit um.
           *
           * NACHBESSERUNG (W2, Pruefung f590385): der `else`-Zweig unten ist neu. Seit dem Auftrag
           * "Mehrfachauswahl" verzichtet `Bearbeitbar` bei einem Klick MIT Zusatztaste bewusst auf
           * `stopPropagation` (Kopfkommentar dieser Datei, "Das stopPropagation ... gilt seither
           * NICHT MEHR ausnahmslos") - das Ereignis bubbelt dann zum `<tr>` und markiert die Zeile.
           * Ein Strg/Shift-Klick GENAU auf diesen Anker loeste dadurch ZWEI Dinge gleichzeitig aus:
           * das native "in neuem Tab oeffnen" (react-router `Link` ruft bei einem modifizierten
           * Klick bewusst KEIN `preventDefault`, siehe `istEinfacherKlick`/`Verweis.tsx`) UND
           * zusaetzlich die Zeilenmarkierung, weil das Ereignis ungehindert weiterbubbelte.
           * Entscheidung: der Anker hatte "in neuem Tab oeffnen" bereits VOR dem Mehrfachauswahl-
           * Auftrag fuer Strg/Shift-Klicks reserviert - eine zweite, unerwartete Wirkung (Markierung)
           * obendrauf widerspraeche diesem etablierten Verhalten. `stopPropagation` im `else`-Zweig
           * verhindert NUR das Bubbeln zu `Bearbeitbar`/`<tr>` (also die Markierung) - das native
           * Tab-Oeffnen haengt an `href`/`target` des gerenderten `<a>`, nicht an der React-
           * Ereignispropagation, und bleibt davon unberuehrt.
           */}
          {/* AUFTRAG 07 (E4): eine in JTL geloeschte Bestellung bekommt KEINEN Tieflink-Anker. Der
              Anker navigiert in den Drawer dieser Bestellung - die dort angebotenen Schreibwege
              setzen einen in JTL vorhandenen Datensatz voraus. Statt des Ankers steht der reine
              Text; die `order-list-name-copy`-Huelle (samt `title`) bleibt, damit die Spalte gleich
              breit und der Tooltip erhalten bleibt. */}
          {schreibgeschuetzt ? (
            label
          ) : (
            <Verweis
              className="tieflink-anker"
              replace
              onClick={(ereignis) => {
                if (istEinfacherKlick(ereignis)) {
                  onOeffnen?.(o.id as string);
                }
                // IMMER stoppen (Auftrag "Klick auf nicht aenderbare Grid-Zelle oeffnet die
                // Bestellung"): die ganze Zelle oeffnet seither ueber `Bearbeitbar` -> `onBearbeiten`.
                // Liesse der Anker das Ereignis hochblubbern, oeffnete ein Klick auf den Anker
                // zweimal. Fuer Strg/Shift gilt weiter das W2-Argument oben (kein Markieren).
                ereignis.stopPropagation();
              }}
              ziel={{
                ...ansicht,
                bestellung: { kennung: kennungVon(o, o.id as string), tab: "main", aufgabe: null, dokument: null },
              }}
            >
              {label}
            </Verweis>
          )}
          {/* Stock-Kennzeichnung (Spec 2026-10-04): reine Anzeige neben dem Anker, `StockMarke.tsx`. */}
          <StockMarke order={o} />
          {/* Neue Artikel (JTL kennt noch keine Lagerbewegung): Marke "Neu", `NeuerArtikelMarke.tsx`. */}
          <NeuerArtikelMarke order={o} />
          {/* Split-Kennzeichnung: neben dem Anker, nicht darin (eigener Klick, `SplitMarke.tsx`). */}
          <BestellungSplitMarke order={o} />
        </span>,
        // AUFTRAG 07 (E4): `is-block` (display:block; die Huelle fuellt die Zelle, damit die ganze
        // Zelle klickbar ist) gehoert zur Bearbeitungs-Huelle - ohne sie bleibt es ein `<span>`.
        schreibgeschuetzt ? undefined : "is-block",
      );
    case "due":
      return huelle("Fälligkeitsdatum", <Datum wert={orderListDue(o)} markDue />);
    case "supplier": {
      // index.html:7738 prueft `esc(o.supplier) || '<span…>'`, also den TEXT und nicht den Wert:
      // eine Lieferantenangabe `0` ist als Text `"0"` und damit wahrhaftig - eine Pruefung auf
      // `o.supplier` allein liefe an dieser Stelle in den Gedankenstrich.
      const text = String(o.supplier ?? "");
      // NEUBAU ohne Bestandsvorbild (EU/China-Faehnchen, `lib/laender.ts`): dieselbe Aufloesung wie
      // `orderCurrency(o, lieferanten)?.eur` (`lib/suppliers.ts`) - der bereits geladene lokale
      // Store, KEIN eigener Nachschlag je Zeile gegen JTL. Ohne aufgeloesten Lieferanten (kein
      // Store, keine Uebereinstimmung) liefert `supplierRegion(undefined)` "other" - kein Faehnchen.
      const region = supplierRegion(orderSupplier(o, lieferanten)?.iso);
      // Feature "Luftfracht/Seefracht/Strassenfracht-Filter im Bestellhub" (2026-08-27, ergaenzt um
      // "road" 2026-09): das Transport-Icon steht VOR der Flagge (Reihenfolge in der Zelle:
      // Transport-Icon -> Flagge (falls eu/china) -> Lieferantenname). Anders als die Flagge (kein
      // Faehnchen fuer "other") erscheint dieses Icon bei JEDEM Lieferanten - jede Bestellung ist
      // See, Flug ODER Strasse, `transportMode` fehlend/`undefined`/unbekannt zaehlt als "see" (DB-
      // Default, `lib/types.ts#Order.transportMode`). NICHT im leeren Fall (`text` faellt auf den
      // "–"-Platzhalter zurueck, siehe unten) - gemeldet unter Punkt 6a der Nachbesserung zum
      // Transportart-Filter (nach Commit c9da74e), CHANGELOG-Satz dort entsprechend entschaerft ("bei
      // jedem Lieferanten" statt "immer"). `title` sitzt auf dem umschliessenden `<span>`, nicht auf
      // `<Ikone>` selbst - dieselbe Bauart wie das See/Flug-Badge in
      // `TimelinePage/ZeitstrahlSeite.tsx` (`.tlp-badge-air`): `Ikone` fuehrt keine `title`-Prop.
      // `role="img"` + `aria-label` NACHGETRAGEN (Punkt 6b derselben Nachbesserung): `Ikone` zeichnet
      // ihr SVG immer mit `aria-hidden="true"` (siehe `Ikone.tsx`), anders als die Flagge gibt es
      // hier auch keine redundante Text-Spalte, die `transportMode` fuer Screenreader mitliefert -
      // ohne eigenen zugaenglichen Namen am umschliessenden `<span>` waere dieses Icon fuer
      // Screenreader komplett unsichtbar (kein `title` allein macht ein Element zugaenglich).
      const transportartIcon =
        o.transportMode === "air" ? "plane" : o.transportMode === "road" ? "truck" : "ship";
      const transportartBeschriftung =
        o.transportMode === "air" ? "Luftfracht" : o.transportMode === "road" ? "Straßenfracht" : "Seefracht";
      return huelle(
        "Lieferant",
        text ? (
          <>
            <span
              className="order-list-supplier-transportart"
              title={transportartBeschriftung}
              role="img"
              aria-label={transportartBeschriftung}
            >
              <Ikone name={transportartIcon} />
            </span>
            {region === "eu" || region === "china" ? (
              <Flagge
                region={region}
                className="order-list-supplier-flagge"
                title={region === "eu" ? "EU-Lieferant" : "Lieferant in China"}
                aria-hidden="true"
              />
            ) : null}
            {text}
          </>
        ) : o.supplier === null && schreibgeschuetzt ? (
          <span className="order-list-unknown">unbekannt</span>
        ) : (
          <span className="order-list-empty">–</span>
        ),
      );
    }
    case "status":
      return huelle(
        "Status",
        <StatusPille definitionen={definitionen} status={o.status as string} />,
      );
    case "invoiceNo":
      return huelle(
        "Rechnungsnummer",
        o.invoiceNo ? String(o.invoiceNo) : <span className="order-list-empty">–</span>,
      );
    case "value":
      return huelle(
        "Bestellwert",
        orderTotal(o) ? (
          fmtMoney(o, orderTotal(o), lieferanten)
        ) : (
          <span className="order-list-empty">–</span>
        ),
      );
    case "etdPi":
      return huelle("PI-ETD", <Datum wert={o.etdPi} />);
    case "etd":
      return huelle("ETD", <Datum wert={o.etd} />);
    case "eta":
      return huelle("ETA", <Datum wert={o.eta} />);
    case "zoll":
      // Spalte „Zollinstruktionen anstoßen": die Plakette steht, wenn der Task „Zollinstruktionen an
      // Spedition senden (Outlook)" erledigt ist (`lib/zollinstruktionen.ts`). Nur in diesem Fall gerechnet, nicht für
      // jede Zelle der Zeile.
      return huelle(
        "Zollinstruktionen anstoßen",
        zollinstruktionenAngestossen(o) ? <ZollPille /> : <span className="order-list-empty">–</span>,
      );
    case "payment":
      return huelle("Zahlung anstoßen", payment);
    case "booked":
      return huelle("Ware eingebucht", booked);
    case "dsvShipmentNo":
      // Wie `booked`: abgeleiteter, nicht editierbarer Wert (`shipments.dsv_shipment_no` ueber
      // `order_shipments`, siehe Order.dsvShipmentNo). `huelle()` haelt die Zelle trotzdem
      // anklickbar - `orderListEditorRegel` liefert dafuer ueber `NICHT_BEARBEITBAR`
      // (lib/orderListEditor.ts) eine Begruendung statt eines Editors, der ins Leere schriebe.
      return huelle(
        "Trackingnummer",
        o.dsvShipmentNo ? (
          o.dsvShipmentNo as string
        ) : (
          <span className="order-list-empty">–</span>
        ),
      );
    default: {
      // index.html:7750, `esc(o.customFields?.[key] || '') || '<span…>'` - dieselbe Textpruefung
      // wie bei `supplier`, siehe dort.
      const wert = String((o.customFields as Record<string, unknown>)?.[key] || "");
      return huelle("Eigenes Listenfeld", wert ? wert : <span className="order-list-empty">–</span>);
    }
  }
}
